<template>
  <header class="topbar">
    <div class="topbar-left">
      <button class="hamburger" @click="$emit('toggle-nav')" aria-label="Ouvrir le menu">
        <span></span><span></span><span></span>
      </button>
      <div class="breadcrumb">
        <router-link to="/">{{ $t('breadcrumb.home') }}</router-link>
        <span class="separator">/</span>
        <span class="current-page">{{ currentPage }}</span>
      </div>
    </div>

    <div class="topbar-right">
      <div class="user-menu">
        <button @click="toggleUserMenu" class="user-button">
          <span class="avatar">{{ userInitials }}</span>
          <span class="user-info">
            <span class="user-name">{{ userName }}</span>
            <span class="user-role">{{ userRole }}</span>
          </span>
        </button>
        <div v-if="showUserMenu" class="dropdown-menu">
          <router-link to="/settings" class="menu-item">
            {{ $t('auth.profile') }}
          </router-link>
          <button @click="logout" class="menu-item logout">
            {{ $t('menu.logout') }}
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/authStore'

const router = useRouter()
const authStore = useAuthStore()
const showUserMenu = ref(false)

const props = defineProps({
  currentPage: String
})

defineEmits(['toggle-nav'])

const userName = computed(() => {
  const user = authStore.user
  if (user) {
    return user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.username
  }
  return 'Utilisateur'
})

const userInitials = computed(() => {
  const name = userName.value
  return name
    .split(' ')
    .slice(0, 2)
    .map(n => n[0])
    .join('')
    .toUpperCase()
})

const userRole = computed(() => {
  const profile = authStore.profile
  return profile?.department || 'Admin'
})

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value
}

const logout = async () => {
  await authStore.logout()
  router.push('/login')
}
</script>

<style scoped>
.topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
  background-color: white;
  border-bottom: 1px solid var(--color-border);
  padding: 0 var(--spacing-xl);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 50;
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  min-width: 0;
}

/* ─── Hamburger (mobile / tablet) ─── */
.hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 42px;
  height: 42px;
  padding: 0 9px;
  border: none;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease;
  flex-shrink: 0;
}
.hamburger:hover { background: var(--color-gray-100); }
.hamburger span {
  display: block;
  height: 2.5px;
  width: 100%;
  background: var(--color-text);
  border-radius: 2px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  font-size: 0.9375rem;
  min-width: 0;
}

.breadcrumb a {
  color: var(--color-primary);
  font-weight: 500;
  transition: color var(--transition-base);
}

.breadcrumb a:hover {
  color: var(--color-secondary);
}

.breadcrumb .separator {
  color: var(--color-gray-400);
}

.breadcrumb .current-page {
  color: var(--color-gray-600);
  font-weight: 600;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
}

.user-menu {
  position: relative;
}

.user-button {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-md);
  transition: background-color var(--transition-base);
}

.user-button:hover {
  background-color: var(--color-gray-100);
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.9rem;
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.user-name {
  font-weight: 600;
  color: var(--color-text);
  font-size: 0.9375rem;
}

.user-role {
  font-size: 0.8125rem;
  color: var(--color-gray-500);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  margin-top: var(--spacing-sm);
  z-index: 1000;
}

.menu-item {
  display: block;
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  text-align: left;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--color-text);
  font-weight: 500;
  transition: background-color var(--transition-base);
}

.menu-item:first-child {
  border-radius: var(--radius-md) var(--radius-md) 0 0;
}

.menu-item:last-child {
  border-radius: 0 0 var(--radius-md) var(--radius-md);
}

.menu-item:hover {
  background-color: var(--color-gray-100);
}

.menu-item.logout {
  border-top: 1px solid var(--color-border);
  color: var(--color-danger);
}

/* Show hamburger once the sidebar becomes an off-canvas drawer */
@media (max-width: 1024px) {
  .hamburger { display: flex; }
}

@media (max-width: 768px) {
  .topbar {
    padding: 0 var(--spacing-lg);
    height: 60px;
  }

  .breadcrumb {
    font-size: 0.875rem;
  }

  .user-button {
    padding: var(--spacing-xs) var(--spacing-sm);
  }

  .user-info {
    display: none;
  }
}

@media (max-width: 480px) {
  .topbar { padding: 0 var(--spacing-md); }
  .breadcrumb a { display: none; }
  .breadcrumb .separator { display: none; }
}
</style>
